import { useEffect, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { Pagina } from '@malgoos/shared';
import { get, getTodos } from '../nucleo/api';
import { hora } from '../nucleo/formato';
import type { Disponibilidade, Recurso, Servico } from '../modules/agenda/tipos';
import { Carregando, Erro } from './estados';
import { CampoControlado, type DefCampo } from './formulario';

/**
 * Peças da Agenda que outras telas reaproveitam (a tarefa do tipo Reunião do CRM, lista B de 30/10 item 23): consultas de
 * recursos, serviços e disponibilidade, o seletor de horários livres, a caixa de marcar e os campos personalizados do
 * agendamento. Ficam aqui porque tela de módulo não importa tela de outro módulo; a Agenda usa as mesmas.
 */

/** Todas as chaves de consulta do módulo começam com 'agenda': um invalidate limpa tudo. */
export const CHAVE = 'agenda';

/**
 * Páginas de 200 de uma listagem, até `maxPaginas`, a partir de `cursor` (nada = do começo); `parcial` diz se o servidor ainda
 * tinha mais e `cursor` é de onde continuar.
 */
export async function buscarPaginas<T>(caminho: string, params: Record<string, unknown>, maxPaginas = 10, inicio?: string): Promise<{ itens: T[]; parcial: boolean; cursor: string | null }> {
  // O laço de cursor é o do `getTodos` (páginas de 200, até `maxPaginas`); o cursor que sobra diz que o servidor tinha mais.
  const r = await getTodos<T, Pagina<T>>(caminho, inicio ? { ...params, cursor: inicio } : params, maxPaginas);
  return { itens: r.itens, parcial: !!r.proximo_cursor, cursor: r.proximo_cursor ?? null };
}

/** Junta as páginas de uma listagem paginada (limite máximo de 200 por página, no máximo 10 páginas). */
export async function buscarTudo<T>(caminho: string, params: Record<string, unknown>): Promise<T[]> {
  return (await buscarPaginas<T>(caminho, params)).itens;
}

export function useRecursos(somenteAtivos = true) {
  return useQuery({ queryKey: [CHAVE, 'recursos', somenteAtivos], queryFn: () => buscarTudo<Recurso>('/agenda/recursos', { ativo: somenteAtivos ? 'true' : undefined }), staleTime: 30_000 });
}

export function useServicos(somenteAtivos = true) {
  return useQuery({ queryKey: [CHAVE, 'servicos', somenteAtivos], queryFn: () => buscarTudo<Servico>('/agenda/servicos', { ativo: somenteAtivos ? 'true' : undefined }), staleTime: 30_000 });
}

export function useDisponibilidade(servicoId: string, recursoId: string, dia: string) {
  return useQuery({
    queryKey: [CHAVE, 'disponibilidade', servicoId, recursoId, dia],
    queryFn: () => get<Disponibilidade>('/agenda/disponibilidade', { servico_id: servicoId, recurso_id: recursoId, de: dia, ate: dia }),
    enabled: !!servicoId && !!recursoId && /^\d{4}-\d{2}-\d{2}$/.test(dia),
  });
}

/** AAAA-MM-DD do relógio local do navegador. */
export function diaLocal(d: Date): string {
  const dois = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${dois(d.getMonth() + 1)}-${dois(d.getDate())}`;
}


/** Caixa de marcar no padrão das telas da Agenda (cancelar a série, lembretes), com a ajuda ligada por aria-describedby. */
export function CaixaMarcar({ id, rotulo, ajuda, marcado, aoMudar, desabilitado }: { id: string; rotulo: string; ajuda?: ReactNode; marcado: boolean; aoMudar: (v: boolean) => void; desabilitado?: boolean }) {
  return (
    <div className="flex min-w-0 flex-col gap-1">
      <label htmlFor={id} className={`flex items-center gap-2 text-sm ${desabilitado ? 'cursor-not-allowed text-tinta-3' : 'cursor-pointer text-tinta-1'}`}>
        <input id={id} type="checkbox" checked={marcado} disabled={desabilitado} aria-describedby={ajuda ? `${id}-ajuda` : undefined} onChange={(e) => aoMudar(e.target.checked)} />
        {rotulo}
      </label>
      {ajuda && (
        <p id={`${id}-ajuda`} className="text-xs text-tinta-3">
          {ajuda}
        </p>
      )}
    </div>
  );
}

/** Campos personalizados do agendamento (review r3 spec): mesmos controles dos outros formulários, valores em `extras`. */
export function CamposExtrasAgendamento({
  defs,
  valores,
  erros,
  aoMudar,
}: {
  defs: DefCampo[];
  valores: Record<string, unknown>;
  erros: Record<string, string>;
  aoMudar: (chave: string, v: unknown) => void;
}) {
  if (!defs.length) return null;
  return (
    <>
      {defs.map((c) => (
        <div key={c.nome} className="sm:col-span-3">
          <CampoControlado def={c} valor={valores[c.nome.slice(7)]} erro={erros[c.nome] ?? erros[c.nome.slice(7)]} aoMudar={(v) => aoMudar(c.nome.slice(7), v)} />
        </div>
      ))}
    </>
  );
}


/**
 * Horários livres do dia para um serviço e um recurso, vindos de /agenda/disponibilidade.
 * `preferido` (clique no calendário) escolhe sozinho o primeiro horário livre dessa hora ou depois.
 */
export function SeletorHorario({
  servicoId,
  recursoId,
  dia,
  valor,
  aoEscolher,
  preferido,
}: {
  servicoId: string;
  recursoId: string;
  dia: string;
  valor: string;
  aoEscolher: (inicio: string) => void;
  preferido?: Date | null;
}) {
  const { t } = useTranslation('agenda');
  const q = useDisponibilidade(servicoId, recursoId, dia);
  const slots = q.data?.slots;

  useEffect(() => {
    if (!slots || valor) return;
    if (preferido && diaLocal(preferido) === dia) {
      const alvo = slots.find((s) => new Date(s.inicio).getTime() >= preferido.getTime());
      if (alvo) aoEscolher(alvo.inicio);
    }
  }, [slots, valor, preferido, dia, aoEscolher]);

  // Horário escolhido que deixou de ser livre (outro dia, outro serviço ou ocupado agora): limpa a escolha.
  useEffect(() => {
    if (slots && valor && !slots.some((s) => s.inicio === valor)) aoEscolher('');
  }, [slots, valor, aoEscolher]);

  if (!servicoId || !recursoId || !dia) return <p className="text-sm text-tinta-3">{t('horarios.ajuda')}</p>;
  if (q.isPending) return <Carregando />;
  if (q.isError) return <Erro erro={q.error} aoTentar={() => q.refetch()} />;
  if (!slots?.length) return <p className="rounded-lg border border-borda px-3 py-3 text-sm text-tinta-3">{t('horarios.semHorarios')}</p>;
  return (
    <div role="radiogroup" aria-label={t('horarios.titulo')} className="flex flex-wrap gap-2">
      {slots.map((s) => {
        const escolhido = s.inicio === valor;
        return (
          <button
            key={s.inicio}
            type="button"
            role="radio"
            aria-checked={escolhido}
            onClick={() => aoEscolher(s.inicio)}
            className={`numero h-9 min-w-16 rounded-lg border px-3 text-sm font-medium ${escolhido ? 'border-acento-forte bg-acento-suave text-acento-forte' : 'border-tinta-3 text-tinta-2 hover:bg-sup-2 hover:text-tinta-1'}`}
          >
            {hora(s.inicio)}
          </button>
        );
      })}
    </div>
  );
}
